import { useContext, useEffect, useMemo, useState } from "react"
import { Client } from "@xmpp/client"
import { subDays } from "date-fns"
import AdHocCommands from "@/api/adHocCommands"
import { parseHybridJsonResponse } from "@/components/cmrReport/parseHybridJsonResponse"
import type { UnlinkedDocument } from "@/components/cmrReport/useCmrReportData"
import type { DeliveryOrderOption } from "@/components/cmr/LinkDeliveryOrderDialog"
import { driverJid } from "@/jidUtils"
import { useAppSelector } from "@/reducers/hooks"
import type { DocumentLink } from "@/reducers/messagesSlice"
import { gpsDocumentLabel } from "@/lib/gpsDocumentLinks"
import type { RootState } from "@/reducers/store"
import { XmppContext } from "@/stream/xmppClient"

export type OrderDocument = {
  url: string
  fileName: string
  kind: "order" | "cmr"
}

const CMR_LOOKBACK_DAYS = 7
const NO_DOCUMENTS: OrderDocument[] = []
const NO_LINKS: DocumentLink[] = []

const fileNameOf = (url: string) => url.split("/").pop() ?? url

const fetchLinkedCmrs = async (
  client: Client,
  order: DeliveryOrderOption,
): Promise<OrderDocument[]> => {
  const { documents = [] } = await parseHybridJsonResponse<{
    documents?: UnlinkedDocument[]
  }>(
    await AdHocCommands.getDocumentsByMuc(
      client,
      order.chatJid,
      subDays(new Date(order.timestamp), CMR_LOOKBACK_DAYS).toISOString(),
      new Date().toISOString(),
    ),
  )
  return documents.flatMap((document) =>
    document.fileUrl &&
    document.documentLink?.deliveryOrderMessageId === order.messageId
      ? [
          {
            url: document.fileUrl,
            fileName: document.fileName ?? fileNameOf(document.fileUrl),
            kind: "cmr" as const,
          },
        ]
      : [],
  )
}

const fetchOrderDocuments = async (
  client: Client,
  chatJid: string,
): Promise<{ order?: DeliveryOrderOption; documents: OrderDocument[] }> => {
  const { orders = [] } = await parseHybridJsonResponse<{
    orders?: DeliveryOrderOption[]
  }>(await AdHocCommands.getDriverDeliveryOrders(client, driverJid(chatJid), 1))
  const [order] = orders
  if (!order) return { documents: [] }
  const cmrs = await fetchLinkedCmrs(client, order).catch((error) => {
    console.error("Failed to fetch the order's CMR:", error)
    return []
  })
  return {
    order,
    documents: [
      ...cmrs,
      {
        url: order.fileUrl,
        fileName: order.fileName || fileNameOf(order.fileUrl),
        kind: "order",
      },
    ],
  }
}

// GPS links name the order by its DB ID, which wizard's archive lookup does
// not match. Read them from the chat via the order message's delivery-order-id.
const selectGpsLinks =
  (chatJid: string, orderMessageId: string | undefined) =>
  (state: RootState) => {
    const chat = state.messages[chatJid]
    const deliveryOrderId = orderMessageId
      ? chat?.messageIdsWithDeliveryOrder?.[orderMessageId]?.deliveryOrderId
      : undefined
    return (
      (deliveryOrderId &&
        chat?.documentLinksByDeliveryOrderId?.[deliveryOrderId]) ||
      NO_LINKS
    )
  }

// Two counts, not one summed together: a delivery order arriving in the
// same tick a document link is removed elsewhere would cancel out to the
// same total, silently skipping the refetch. Kept as two independent
// dependencies instead, so either one changing is enough.
const selectDeliveryOrdersCount = (chatJid: string) => (state: RootState) =>
  Object.keys(state.messages[chatJid]?.messageIdsWithDeliveryOrder ?? {}).length

const selectDocumentLinksCount = (chatJid: string) => (state: RootState) =>
  Object.values(
    state.messages[chatJid]?.documentLinksByDeliveryOrderMessageId ?? {},
  ).flat().length

export const useOrderDocuments = (chatJid: string): OrderDocument[] => {
  const { client } = useContext(XmppContext)
  const deliveryOrdersCount = useAppSelector(selectDeliveryOrdersCount(chatJid))
  const documentLinksCount = useAppSelector(selectDocumentLinksCount(chatJid))
  const [loaded, setLoaded] = useState<{
    chatJid: string
    order?: DeliveryOrderOption
    documents: OrderDocument[]
  }>()
  const forChat = loaded?.chatJid === chatJid ? loaded : undefined
  const gpsLinks = useAppSelector(
    selectGpsLinks(chatJid, forChat?.order?.messageId),
  )

  useEffect(() => {
    if (!client) return
    let current = true
    fetchOrderDocuments(client, chatJid)
      .then((result) => current && setLoaded({ chatJid, ...result }))
      .catch((error) =>
        console.error("Failed to fetch the driver's order:", error),
      )
    return () => {
      current = false
    }
  }, [client, chatJid, deliveryOrdersCount, documentLinksCount])

  return useMemo(() => {
    if (!forChat) return NO_DOCUMENTS
    if (gpsLinks.length === 0) return forChat.documents
    const gpsCmrs = gpsLinks
      .filter(
        (link) => !forChat.documents.some((doc) => doc.url === link.cmrFileUrl),
      )
      .map((link) => ({
        url: link.cmrFileUrl,
        fileName: gpsDocumentLabel(link),
        kind: "cmr" as const,
      }))
    return [...gpsCmrs, ...forChat.documents]
  }, [forChat, gpsLinks])
}
